import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/utils-vr";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import AdSlot from "@/components/vr/AdSlot";
import Seo, { blogPostLd, breadcrumbLd, faqLd } from "@/components/vr/Seo";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);
  return (
    <div className="vr-container my-8">
      <Seo
        title="Blog — Bihar Board Preparation Guides & Tips"
        description="बिहार बोर्ड परीक्षा की तैयारी, टिप्स, टॉपर स्ट्रेटेजी एवं छात्र-हित लेखों का सम्पूर्ण संग्रह।"
        canonical="/blog"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }])}
      />
      <Breadcrumbs items={[{ label: "Blog" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Blog</h1>
      <p className="text-slate-600 mt-2 font-hindi">बिहार बोर्ड परीक्षा की तैयारी, टिप्स एवं छात्र-हित लेख।</p>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="vr-card overflow-hidden" data-testid={`blog-item-${p.slug}`}>
            {p.featured_image && <img loading="lazy" src={resolveMediaUrl(p.featured_image)} alt={`${p.title} — VR Classes blog cover`} className="w-full aspect-video object-cover" />}
            <div className="p-4">
              <span className="vr-chip">{p.category}</span>
              <h2 className="mt-2 font-display font-bold text-[#0F172A] text-base">{p.title}</h2>
              <div className="mt-1 text-sm text-slate-600 line-clamp-2">{p.excerpt}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)); }, [slug]);
  if (!post) return <div className="vr-container my-10 text-slate-500">Loading...</div>;
  const canonical = `/blog/${post.slug}`;
  const title = post.meta_title || post.title;
  const desc = post.meta_description || post.excerpt || "";
  const image = resolveMediaUrl(post.featured_image);
  const bc = breadcrumbLd([
    { name: "Home", url: "/" }, { name: "Blog", url: "/blog" }, { name: post.title, url: canonical },
  ]);
  const post_ld = blogPostLd({
    title: post.title, description: desc, url: canonical, image,
    author: post.author, published: post.published_at || post.created_at, modified: post.updated_at,
  });
  const ld = [bc, post_ld];
  if (post.faqs && post.faqs.length) ld.push(faqLd(post.faqs));
  return (
    <div className="vr-container my-8 max-w-3xl">
      <Seo title={title} description={desc} canonical={canonical} image={image} type="article" jsonLd={ld} articleMeta={{ published_at: post.published_at || post.created_at, updated_at: post.updated_at, author: post.author, tags: post.tags }} />
      <Breadcrumbs items={[{ to: "/blog", label: "Blog" }, { label: post.title }]} />
      <article className="vr-card p-6 sm:p-8">
        {post.featured_image && <img src={resolveMediaUrl(post.featured_image)} alt={`${post.title} — featured image`} className="w-full rounded-lg mb-4 aspect-video object-cover" />}
        <span className="vr-chip">{post.category}</span>
        <h1 className="mt-2 font-display font-extrabold text-3xl text-[#0F172A]">{post.title}</h1>
        <div className="mt-1 text-sm text-slate-500">By {post.author}</div>
        <AdSlot id="blog-top" />
        <div className="prose-vr font-hindi whitespace-pre-line mt-4">{post.content}</div>
        <AdSlot id="blog-bottom" />
        {post.faqs?.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-xl">FAQs</h2>
            <div className="mt-3 space-y-2">
              {post.faqs.map((f, i) => (
                <details key={i} className="vr-card p-3">
                  <summary className="cursor-pointer font-semibold font-hindi">{f.q}</summary>
                  <p className="mt-2 text-sm font-hindi text-slate-700">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
